/**
 * compress-image.ts
 *
 * Compresses a logo image File to a base64 JPEG data URL.
 *
 * Rules (per PRD §1.11a):
 *  - Maximum dimensions: 300 × 80 px (aspect-ratio preserved, never upscaled)
 *  - JPEG quality: 85 %
 *  - Hard limit: rejects files larger than 10 MB before any processing
 */

const MAX_WIDTH = 300;
const MAX_HEIGHT = 80;
const JPEG_QUALITY = 0.85;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

/**
 * Compresses a logo image file and returns a base64-encoded JPEG data URL.
 *
 * @param file - The image File selected by the user (any browser-supported format).
 * @returns A Promise that resolves with the compressed base64 JPEG data URL.
 * @throws An error if the file exceeds 10 MB or if the canvas API is unavailable.
 */
export async function compressLogoImage(file: File): Promise<string> {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    throw new Error(
      `Logo image must be smaller than 10 MB. The selected file is ${(file.size / (1024 * 1024)).toFixed(1)} MB.`,
    );
  }

  return new Promise<string>((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      // Calculate scaled dimensions — never upscale, always preserve aspect ratio
      let { naturalWidth: w, naturalHeight: h } = img;

      if (w > MAX_WIDTH || h > MAX_HEIGHT) {
        const scaleByWidth = MAX_WIDTH / w;
        const scaleByHeight = MAX_HEIGHT / h;
        const scale = Math.min(scaleByWidth, scaleByHeight);
        w = Math.round(w * scale);
        h = Math.round(h * scale);
      }

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas 2D context is unavailable in this browser.'));
        return;
      }

      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', JPEG_QUALITY));
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load the selected image file.'));
    };

    img.src = objectUrl;
  });
}
